import { Stack } from '@koob/margaret';
import { FieldArray, useField, useFormikContext } from 'formik';
import { Fragment, createElement } from 'react';
import { omit } from 'lodash';
import styled, { useTheme } from 'styled-components';
import { KoobButton, KoobInputDate, KoobInputDateRange } from '@koob/ui';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { createPromotionPeriodFactory } from 'utils/promotions';
import { ExperiencePricingTypeEnum, PromotionStateEnum } from '@koob/enums';
import { ErrorMessageWrapper } from 'ui/forms';
import { parseDateOnly, toDateString, dayjs } from '@koob/dates';

const DayButtonSelector = ({
  day,
  selected,
  disabled = false,
  onSelect = () => {},
}) => {
  return (
    <KoobButton
      type="button"
      variant={selected ? 'primary' : 'secondary'}
      size="sm"
      role="switch"
      aria-checked={selected}
      disabled={disabled}
      onClick={() => onSelect({ day: day?.prop, value: !selected })}
      testId={day?.label}
    >
      {day?.label}
    </KoobButton>
  );
};

const LabelWrapper = styled(Stack).attrs({ as: 'label' })``;

const PeriodFieldInput = ({
  name,
  index,
  disabled = false,
  customPeriodFields,
  required,
  onRemove,
}) => {
  const { t } = useTranslation('promotionForm');

  const [state, , { setValue }] = useField(name);
  const [durationDays] = useField('durationDays');
  const [pricingType] = useField('pricingType');

  const { values } = useFormikContext();
  const isOperational = values?.state === PromotionStateEnum.OPERATIONAL;

  const [, startMeta] = useField(`${name}.startAt`);
  const [, endMeta] = useField(`${name}.endAt`);
  const [, daysMeta] = useField(`${name}.days`);

  const hasStartError = isOperational
    ? Boolean(startMeta.error)
    : Boolean(startMeta.touched && startMeta.error);
  const hasEndError = isOperational
    ? Boolean(endMeta.error)
    : Boolean(endMeta.touched && endMeta.error);
  const hasDaysError = isOperational
    ? Boolean(daysMeta.error)
    : Boolean(daysMeta.touched && daysMeta.error);

  const days = [
    { prop: 'Mo', label: 'M' },
    { prop: 'Tu', label: 'T' },
    { prop: 'We', label: 'W' },
    { prop: 'Th', label: 'T' },
    { prop: 'Fr', label: 'F' },
    { prop: 'Sa', label: 'S' },
    { prop: 'Su', label: 'S' },
  ];

  const onDaySelect = useCallback(
    ({ day, value: v }) =>
      setValue({
        ...state.value,
        days: {
          ...state.value.days,
          [day]: v,
        },
      }),
    [setValue, state.value],
  );

  const isFitPricing =
    !pricingType?.value || pricingType?.value === ExperiencePricingTypeEnum.FIT;

  return (
    <div>
      <div className="border-b px-6 py-3 flex justify-between items-start gap-3">
        <div className="font-medium text-lg self-center">
          <i className="far fa-dot-circle mr-2" />
          {isFitPricing
            ? t('periods.period', { index: index + 1 })
            : t('periods.departure', { index: index + 1 })}
          {isOperational && <span className="ml-1 text-red-500">*</span>}
        </div>

        <div className="flex items-start gap-2">
          <LabelWrapper>
            <input
              style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }}
              autoFocus
            />

            <div className="flex flex-col justify-center space-y-1">
              {isFitPricing ? (
                <>
                  <KoobInputDateRange
                    block
                    presets={[]}
                    disabled={disabled}
                    value={{
                      from: parseDateOnly(state?.value?.startAt),
                      to: parseDateOnly(state?.value?.endAt),
                    }}
                    onChange={(range) => {
                      if (range === null) {
                        setValue(omit(state.value, ['startAt', 'endAt']));
                      } else {
                        setValue({
                          ...state.value,
                          startAt: range.from
                            ? toDateString(range.from)
                            : undefined,
                          endAt: range.to ? toDateString(range.to) : undefined,
                        });
                      }
                    }}
                  />
                  {(hasStartError || hasEndError) && (
                    <ErrorMessageWrapper style={{ marginTop: '0.25rem' }}>
                      {t('errors:required')}
                    </ErrorMessageWrapper>
                  )}
                </>
              ) : (
                <div className="flex flex-col space-y-1 w-full">
                  <div className="flex space-x-3">
                    <div className="flex flex-col w-full">
                      <KoobInputDate
                        block
                        clearable={false}
                        disabled={
                          disabled || state.value.currentPaxForDeparture
                        }
                        value={parseDateOnly(state?.value?.startAt) ?? null}
                        onChange={(date) => {
                          setValue({
                            ...state.value,
                            startAt: date ? toDateString(date) : null,
                            endAt: date
                              ? toDateString(
                                  dayjs(date).add(
                                    Math.max(0, durationDays.value - 1),
                                    'day',
                                  ),
                                )
                              : null,
                          });
                        }}
                      />
                      {hasStartError && (
                        <ErrorMessageWrapper style={{ marginTop: '0.25rem' }}>
                          {t('errors:required')}
                        </ErrorMessageWrapper>
                      )}
                    </div>

                    <div className="flex flex-col w-full">
                      <KoobInputDate
                        block
                        disabled
                        value={parseDateOnly(state?.value?.endAt) ?? null}
                        onChange={() => {}}
                      />
                      {hasEndError && (
                        <ErrorMessageWrapper style={{ marginTop: '0.25rem' }}>
                          {t('errors:required')}
                        </ErrorMessageWrapper>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </LabelWrapper>

          {onRemove && (
            <KoobButton
              type="button"
              variant="danger"
              disabled={disabled}
              onClick={onRemove}
              aria-label={t('misc:delete')}
              data-testid="remove-period"
            >
              <i className="fa-light fa-trash" style={{ fontSize: 20 }} />
            </KoobButton>
          )}
        </div>
      </div>

      <div className="p-6">
        {isFitPricing && (
          <div className="flex flex-col">
            <Stack direction="row" alignY="center" style={{ width: '100%' }}>
              <span>
                {t('periods.days')}
                {required && <span className="ml-1 text-red-500">*</span>}
              </span>

              <Stack
                alignY="end"
                alignX="center"
                direction="row"
                gutterSize={0.375}
                style={{ marginLeft: 'auto' }}
              >
                {days.map((day) => (
                  <DayButtonSelector
                    disabled={disabled}
                    key={day?.prop}
                    day={day}
                    selected={state.value.days[day.prop]}
                    onSelect={onDaySelect}
                  />
                ))}
              </Stack>
            </Stack>
            {hasDaysError && (
              <ErrorMessageWrapper
                style={{ marginTop: '0.25rem', textAlign: 'right' }}
              >
                {t('errors:required')}
              </ErrorMessageWrapper>
            )}
          </div>
        )}

        {customPeriodFields && (
          <Stack
            direction="row"
            alignY="center"
            style={{ width: '100%' }}
            id="t"
          >
            {createElement(customPeriodFields, { index: index })}
          </Stack>
        )}
      </div>
    </div>
  );
};

const PeriodsField = ({
  name,
  disabled = false,
  customPeriodFields,
  required,
  bordered = false,
  canRemoveLastElement,
}) => {
  const theme = useTheme();
  const { t } = useTranslation('promotionForm');
  const [field] = useField(name);

  return (
    <div className={bordered ? 'p-5 border rounded-lg' : ''}>
      <FieldArray
        name={name}
        render={(helpers) => {
          return (
            <>
              <div className="flex-col space-y-3">
                {field?.value?.map((item, index) => {
                  const canRemove =
                    (index !== 0 || canRemoveLastElement) &&
                    !item?.currentPaxForDeparture;

                  return (
                    <div className="border rounded-lg" key={index}>
                      <PeriodFieldInput
                        name={`${name}.${index}`}
                        index={index}
                        disabled={disabled}
                        customPeriodFields={customPeriodFields}
                        required={required}
                        onRemove={
                          canRemove ? () => helpers.remove(index) : undefined
                        }
                      />
                    </div>
                  );
                })}
              </div>

              {!disabled && (
                <KoobButton
                  variant="soft"
                  size="sm"
                  block
                  style={{ marginTop: theme.spacing(0.75) }}
                  onClick={() => helpers.push(createPromotionPeriodFactory())}
                >
                  <i className="fa-light fa-plus mr-1" />
                  {t('periods.addPeriod')}
                </KoobButton>
              )}
            </>
          );
        }}
      />
    </div>
  );
};

export default PeriodsField;
